import React, { useEffect } from 'react';
import { EntryPointContainer } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { MAIN_CONTENT } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import ApdexProvider from '@atlassian/jira-providers-spa-apdex-analytics/src/provider.tsx';
import {
	ReportSSRFailOnUse,
	DisableSSRFailDetection,
} from '@atlassian/jira-ssr-render-guard/src/index.tsx';
import {
	SpaStateContainer,
	SpaStateAppStartWithMatchCheck,
} from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import type {
	Route,
	Location,
	HistoryAction,
	Match,
	Query,
} from '@atlassian/react-resource-router';

const LOCATION = 'route-entry-point-container';
export const EmptySkeleton = () => null;

/**
 * Orchestrates the rendering process for a route's entry point within a single-page application.
 * It intelligently manages page transitions, displaying a skeleton screen while the entry point is loading.
 * The component also handles special cases like redirects by incorporating logic to render or skip the
 * entry point accordingly. This ensures a smooth user experience during navigation and page load events.
 */
export const RouteEntryPointContainer = ({
	location,
	route,
	match,
	action,
	query,
}: {
	location: Location;
	route: Route;
	match: Match;
	action: HistoryAction;
	query: Query;
}) => {
	const entryPointReference = usePageLayoutEntryPoint(MAIN_CONTENT);

	// in order to show a spinner while page loading -> `route.skeleton=Spinner`
	const Skeleton = route.skeleton || EmptySkeleton;
	useEffect(() => {
		if (route.isRedirect) {
			log.safeWarnWithoutCustomerData(
				LOCATION,
				`EntryPoint was used inside a redirect component: ${route.name}`,
			);
		}
	}, [route.isRedirect, route.name]);
	if (!entryPointReference) {
		return null;
	}

	let pageId: string = route.name;
	if (route.pageId) {
		if (typeof route.pageId === 'function') {
			pageId = route.pageId({
				route,
				action,
				location,
				match,
				query,
			});
		} else {
			pageId = route.pageId;
		}
	}

	const fallback = fg('jfp_magma_jira-ssr-fail-detection') ? (
		<>
			<Skeleton />
			<ReportSSRFailOnUse pageId={pageId} />
		</>
	) : (
		<Skeleton />
	);

	const page = fg('jfp_magma_jira-ssr-fail-detection') ? (
		<DisableSSRFailDetection>
			<EntryPointContainer
				entryPointReference={entryPointReference}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				props={{} as never}
			/>
		</DisableSSRFailDetection>
	) : (
		<EntryPointContainer
			entryPointReference={entryPointReference}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			props={{} as never}
		/>
	);

	return route.isRedirect ? (
		<EntryPointReferencePlaceholder
			entryPointReference={entryPointReference}
			fallback={<Skeleton />}
			name="route_entry_point_redirect"
		>
			{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
			<EntryPointContainer entryPointReference={entryPointReference} props={{} as never} />
		</EntryPointReferencePlaceholder>
	) : (
		<ApdexProvider pageId={pageId}>
			<SpaStateContainer isGlobal appId={pageId}>
				<SpaStateAppStartWithMatchCheck />
				<EntryPointReferencePlaceholder
					entryPointReference={entryPointReference}
					fallback={fallback}
					name="route_entry_point"
				>
					{page}
				</EntryPointReferencePlaceholder>
			</SpaStateContainer>
		</ApdexProvider>
	);
};
